'use client';

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Copy,
  Check,
  X,
  ArrowsClockwise,
  Prohibit,
} from '@phosphor-icons/react';
import { useToast } from '@/hooks/useToast';
import { useAddMemberToOrganization } from '@/hooks/useAddMemberToOrganization';
import { useUserLogged } from '@/hooks/useUserLogged';
import { ownerOnly } from '@/lib/rbac';
import useOrganizationStore from '@/state/orgState';
import { useSession } from 'next-auth/react';
import {
  getOrCreateInviteToken,
  updateInviteToken,
  revokeInviteToken,
  type ExpiresIn,
  type InviteRole,
} from '@/apis/inviteTokens';
import { tokenizeEmails } from '@/helpers/inviteEmails';
import {
  InviteLinkTeamControl,
  useInviteLinkTeam,
} from '@/components/UserManagement/SubComponents/InviteLinkTeamControl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { inviteRoles } from '@/lib/rbac';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EXPIRY_OPTIONS: { value: ExpiresIn; label: string }[] = [
  { value: '24h', label: '24 hours' },
  { value: '48h', label: '48 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'never', label: 'Never' },
];

const ROLE_OPTIONS: { value: InviteRole; label: string }[] = [
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' },
];

const ACCESS_DENIED_MESSAGE = `${ownerOnly('create admin invite links')}.`;

const formatExpiry = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddMemberModal = ({ isOpen, onClose }: AddMemberModalProps) => {
  const { data: session } = useSession();
  const { user } = useUserLogged();
  const toast = useToast();
  const queryClient = useQueryClient();

  const organization = useOrganizationStore((state) => state.organization);
  const orgUuid = organization?.OrgUUID ?? '';
  const userUuid = user?.UserUUID ?? '';
  const allowedInviteRoles = inviteRoles(user?.Role);
  const canInvite = allowedInviteRoles.length > 0;

  // Email invite state — committed chips plus the in-progress draft.
  const [chips, setChips] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [emailRole, setEmailRole] = useState<InviteRole>('member');

  // Invite link state.
  const [expiresIn, setExpiresIn] = useState<ExpiresIn>('48h');
  const [linkRole, setLinkRole] = useState<InviteRole>('member');
  // Links are keyed on (role, team): the pickers choose which link is shown.
  const { teamKey, teamUuid, setTeamKey } = useInviteLinkTeam();
  const linkKey = { role: linkRole, teamUuid };
  const [copied, setCopied] = useState(false);

  const addMemberMutation = useAddMemberToOrganization(organization?.OrgUUID);

  // Same query key as InviteLinkCard so the cache is shared with Members page.
  const {
    data: tokenRes,
    isPending: isTokenPending,
    isError: isTokenError,
    refetch: refetchToken,
  } = useQuery({
    queryKey: ['inviteToken', orgUuid, userUuid, linkRole, teamKey],
    // Read-only: opening the modal must not mint a token as a side effect —
    // the user explicitly generates one from the empty state.
    queryFn: () =>
      getOrCreateInviteToken(
        orgUuid,
        userUuid,
        expiresIn,
        false,
        linkRole,
        false,
        teamUuid
      ),
    enabled: canInvite && isOpen && !!orgUuid && !!userUuid,
  });

  const regenerate = useMutation({
    mutationFn: (params: { expiresIn: ExpiresIn; role: InviteRole }) =>
      getOrCreateInviteToken(
        orgUuid,
        userUuid,
        params.expiresIn,
        true,
        params.role,
        true,
        teamUuid
      ),
    onSuccess: (data) => {
      if (data.Error === 'access_denied') {
        toast({ title: ACCESS_DENIED_MESSAGE, status: 'error' });
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ['inviteToken', orgUuid, userUuid],
      });
      if (data.Error) {
        toast({
          title: "Couldn't create the invite link. Please try again.",
          status: 'error',
        });
        return;
      }
      toast({ title: 'New invite link created', status: 'success' });
    },
    onError: () => {
      toast({
        title: "Couldn't reset the invite link. Please try again.",
        status: 'error',
      });
    },
  });

  // In-place update: an expiry change without rotating the link, so
  // already-shared links keep working.
  const updateToken = useMutation({
    mutationFn: (changes: { expiresIn: ExpiresIn }) =>
      updateInviteToken(orgUuid, userUuid, linkKey, changes),
    onSuccess: (data) => {
      if (data.Error === 'access_denied') {
        toast({ title: ACCESS_DENIED_MESSAGE, status: 'error' });
      } else if (data.Error) {
        toast({
          title: "Couldn't update the invite link. Please try again.",
          status: 'error',
        });
      } else {
        toast({ title: 'Invite link updated', status: 'success' });
      }
      queryClient.invalidateQueries({
        queryKey: ['inviteToken', orgUuid, userUuid],
      });
    },
    onError: () => {
      toast({
        title: "Couldn't update the invite link. Please try again.",
        status: 'error',
      });
    },
  });

  const revoke = useMutation({
    mutationFn: () => revokeInviteToken(orgUuid, userUuid, linkKey),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['inviteToken', orgUuid, userUuid],
      });
      toast({ title: 'Invite link revoked', status: 'success' });
    },
    onError: () => {
      toast({
        title: "Couldn't revoke the invite link. Please try again.",
        status: 'error',
      });
    },
  });

  // Selects mirror the server's token — local defaults would lie about an
  // existing admin/7d link otherwise.
  // Role and team are not mirrored: they are the link's key and pick it.
  useEffect(() => {
    if (!tokenRes?.Token) return;
    if (tokenRes.ExpiresIn) setExpiresIn(tokenRes.ExpiresIn);
  }, [tokenRes]);

  const inviteLink =
    tokenRes?.Token && typeof window !== 'undefined'
      ? `${window.location.origin}/invite/${tokenRes.Token}`
      : null;

  const isAdminLink = tokenRes?.Role === 'admin';

  const teamCaption = tokenRes?.TeamName
    ? `Joins team ${tokenRes.TeamName}`
    : 'Joins with no team';
  const expiryCaption = tokenRes?.ExpiresAt
    ? `Expires ${formatExpiry(tokenRes.ExpiresAt)}`
    : 'Never expires';

  // ----- Email chips -----

  const commitDraft = (text: string) => {
    const candidates = tokenizeEmails(text);
    if (candidates.length === 0) return;
    setChips((prev) => {
      const seen = new Set(prev.map((chip) => chip.toLowerCase()));
      const fresh = candidates.filter((email) => {
        const key = email.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return [...prev, ...fresh];
    });
    setDraft('');
  };

  const removeChip = (email: string) => {
    setChips((prev) => prev.filter((chip) => chip !== email));
  };

  const handleDraftKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitDraft(draft);
    } else if (e.key === 'Backspace' && draft === '' && chips.length > 0) {
      setChips((prev) => prev.slice(0, -1));
    }
  };

  const handleDraftPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (tokenizeEmails(text).length > 1) {
      e.preventDefault();
      commitDraft(`${draft} ${text}`);
    }
  };

  const invalidChips = chips.filter((chip) => !EMAIL_REGEX.test(chip));
  const pendingEmails = [
    ...chips.filter((chip) => EMAIL_REGEX.test(chip)),
    ...tokenizeEmails(draft).filter((email) => EMAIL_REGEX.test(email)),
  ];
  const sendLabel =
    pendingEmails.length > 1
      ? `Send ${pendingEmails.length} invites`
      : 'Send invite';

  const handleSendInvites = async () => {
    // Pull anything still sitting in the draft into the batch.
    const validEmails = [
      ...new Set(
        [...chips, ...tokenizeEmails(draft)]
          .filter((email) => EMAIL_REGEX.test(email))
          .map((email) => email.toLowerCase())
      ),
    ];
    if (validEmails.length === 0) return;

    const failures: string[] = [];
    for (const email of validEmails) {
      try {
        const res = await addMemberMutation.mutateAsync({
          OrganizationUUID: organization?.OrgUUID || '',
          AccessToken: session?.user?.accessToken || '',
          UserEmail: email,
          Role: emailRole,
          FirstName: '',
          LastName: '',
          InviterUserUUID: user?.UserUUID,
          ExpiresIn: expiresIn,
        });
        // Backend reports business failures (already a member / already
        // invited) inside a 200 payload, not as an HTTP error.
        const data = typeof res === 'string' ? JSON.parse(res) : res;
        if (data?.Error) {
          failures.push(email);
          toast({
            title: `Couldn't invite ${email}`,
            description: data.Error,
            status: 'error',
            duration: 5000,
          });
        }
      } catch {
        // The mutation hook already toasts each failure with its message.
        failures.push(email);
      }
    }

    if (failures.length === 0) {
      toast({
        title:
          validEmails.length > 1
            ? `${validEmails.length} invites sent`
            : 'Invite sent',
        description: 'They’ll get an email with a link to join.',
        status: 'success',
        duration: 3000,
      });
      setChips([]);
      setDraft('');
      onClose();
    } else {
      // Keep only the failed emails so the user can retry or remove them.
      setChips(failures);
      setDraft('');
    }
  };

  // ----- Invite link -----

  const copyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
      toast({
        title: 'Link copied!',
        description: 'Invite link copied to clipboard',
        status: 'success',
        duration: 2000,
      });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy link to clipboard',
        status: 'error',
        duration: 2000,
      });
    }
  };

  const handleExpiryChange = (value: ExpiresIn) => {
    setExpiresIn(value);
    // No active link → nothing to update; the value flows into the next
    // Generate / email send instead.
    if (tokenRes?.Token) updateToken.mutate({ expiresIn: value });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl rounded-lg border border-ds-border-subtlest bg-ds-surface-white shadow-xl">
        <DialogHeader>
          <DialogTitle className="ds-text-title-sm-semibold text-ds-text-darker">
            Invite to {organization?.OrgName || 'your team'}
          </DialogTitle>
          <DialogDescription className="ds-text-body-sm-medium text-ds-text-subtle">
            Bring your teammates into the workspace.
          </DialogDescription>
        </DialogHeader>

        {/* Invite by email */}
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-col gap-0.5">
            <h3 className="ds-text-body-sm-semibold text-ds-text-darker">
              Invite by email
            </h3>
            <p className="ds-text-body-xs-medium text-ds-text-subtle">
              They&apos;ll get an email with a join link.
            </p>
          </div>

          <label htmlFor="invite-emails" className="sr-only">
            Email addresses
          </label>
          <div
            className="flex min-h-[42px] w-full cursor-text flex-wrap items-center gap-1.5 rounded-md border border-ds-border-subtler bg-ds-surface-white px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-ds-brand-subtler"
            onClick={(e) =>
              (
                e.currentTarget.querySelector('input') as HTMLInputElement
              )?.focus()
            }
          >
            {chips.map((chip) => {
              const isValid = EMAIL_REGEX.test(chip);
              return (
                <Badge
                  key={chip}
                  className={
                    isValid
                      ? 'gap-1 rounded-full border-0 bg-ds-brand-lightest text-ds-brand-subtler'
                      : 'gap-1 rounded-full border-0 bg-ds-danger-subtlest text-ds-danger-darker'
                  }
                >
                  {chip}
                  <button
                    type="button"
                    onClick={() => removeChip(chip)}
                    aria-label={`Remove ${chip}`}
                    className="opacity-60 hover:opacity-100"
                  >
                    <X size={12} aria-hidden />
                  </button>
                </Badge>
              );
            })}
            <input
              id="invite-emails"
              type="email"
              autoComplete="off"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleDraftKeyDown}
              onPaste={handleDraftPaste}
              onBlur={() => commitDraft(draft)}
              placeholder={
                chips.length === 0
                  ? 'jane@acme.com, john@acme.com — paste a list or press Enter'
                  : ''
              }
              className="h-7 min-w-[180px] flex-1 bg-transparent ds-text-body-sm-medium text-ds-text-darker placeholder-ds-text-subtle focus:outline-none"
            />
          </div>

          {invalidChips.length > 0 && (
            <p
              aria-live="polite"
              className="ds-text-body-xs-medium text-ds-danger-default"
            >
              {invalidChips.length === 1
                ? `"${invalidChips[0]}" isn't a valid email — fix or remove it.`
                : `${invalidChips.length} entries aren't valid emails — fix or remove them.`}
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <span className="ds-text-body-sm-medium text-ds-text-subtle">
              Invite as
            </span>
            <Select
              value={emailRole}
              onValueChange={(value) => setEmailRole(value as InviteRole)}
              items={ROLE_OPTIONS}
            >
              <SelectTrigger className="w-28" aria-label="Invite role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {ROLE_OPTIONS.filter(
                    (option) =>
                      option.value === 'member' ||
                      allowedInviteRoles.includes(option.value)
                  ).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <span className="ds-text-body-sm-medium text-ds-text-subtle">
              Expires
            </span>
            <Select
              value={expiresIn}
              onValueChange={(value) => {
                if (value != null) handleExpiryChange(value);
              }}
              items={EXPIRY_OPTIONS}
            >
              <SelectTrigger className="w-28" aria-label="Invite expiry">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {EXPIRY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <Button
              onClick={handleSendInvites}
              disabled={
                addMemberMutation.isPending || pendingEmails.length === 0
              }
            >
              {addMemberMutation.isPending && (
                <Loader2 className="animate-spin" />
              )}
              {sendLabel}
            </Button>
          </div>
        </div>

        {/* Or share invite link: a Member mints no links */}
        {canInvite && (
          <div className="flex min-w-0 flex-col gap-3 border-t border-ds-border-subtlest pt-4">
            <div className="flex flex-col gap-0.5">
              <h3 className="ds-text-body-sm-semibold text-ds-text-darker">
                Or share invite link
              </h3>
              <p className="ds-text-body-xs-medium text-ds-text-subtle">
                Anyone with the link can join — billed next month.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <span className="ds-text-body-xs-medium text-ds-text-subtle">
                Joins as
              </span>
              <Select
                value={linkRole}
                onValueChange={(value) => {
                  if (value != null) setLinkRole(value);
                }}
                items={ROLE_OPTIONS}
              >
                <SelectTrigger className="h-8 w-28" aria-label="Link join role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {ROLE_OPTIONS.filter((option) =>
                      allowedInviteRoles.includes(option.value)
                    ).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <InviteLinkTeamControl
                value={teamKey}
                onChange={setTeamKey}
                triggerClassName="h-8 w-40"
              />
            </div>

            {isTokenPending ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-9 w-full rounded-md bg-ds-gray-subtlest" />
                <Skeleton className="h-7 w-2/3 rounded-md bg-ds-gray-subtlest" />
              </div>
            ) : inviteLink ? (
              <>
                <div className="flex items-center gap-2">
                  <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md bg-ds-gray-subtlest px-3">
                    <span className="ds-text-code-sm truncate text-ds-text-default">
                      {inviteLink}
                    </span>
                    {isAdminLink && (
                      <Badge className="shrink-0 rounded-sm border-0 bg-ds-warning-subtlest text-ds-warning-darker">
                        Admin link
                      </Badge>
                    )}
                  </div>
                  <Button
                    onClick={copyInviteLink}
                    variant="outline"
                    size="sm"
                    className="h-9"
                  >
                    {copied ? (
                      <Check aria-hidden className="text-ds-success-default" />
                    ) : (
                      <Copy aria-hidden />
                    )}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <span className="ds-text-body-xs-medium text-ds-text-subtle">
                    Expires
                  </span>
                  <Select
                    value={expiresIn}
                    onValueChange={(value) => {
                      if (value != null) handleExpiryChange(value);
                    }}
                    items={EXPIRY_OPTIONS}
                  >
                    <SelectTrigger
                      className="h-8 w-28"
                      aria-label="Link expiry"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {EXPIRY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <span className="flex-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      regenerate.mutate({ expiresIn, role: linkRole })
                    }
                    disabled={regenerate.isPending}
                    aria-label="Rotate invite link"
                  >
                    <ArrowsClockwise aria-hidden />
                    Rotate
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-ds-danger-default hover:text-ds-danger-default"
                    onClick={() => revoke.mutate()}
                    disabled={revoke.isPending}
                    aria-label="Revoke invite link"
                  >
                    <Prohibit aria-hidden />
                    Revoke
                  </Button>
                </div>
                <p className="ds-text-body-xs-medium text-ds-text-subtler">
                  {teamCaption}. {expiryCaption}
                </p>
              </>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="ds-text-body-sm-medium text-ds-text-subtle">
                  {isTokenError
                    ? "Couldn't load invite link"
                    : 'No active link — generate one to share.'}
                </p>
                {isTokenError ? (
                  <Button
                    onClick={() => refetchToken()}
                    variant="outline"
                    size="sm"
                  >
                    Retry
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() =>
                      regenerate.mutate({ expiresIn, role: linkRole })
                    }
                    disabled={regenerate.isPending}
                  >
                    {regenerate.isPending && (
                      <Loader2 className="animate-spin" />
                    )}
                    Generate new link
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
